import {
  Card,
  message,
  Spin,
  Tag,
  Button,
  Col,
  Row,
  DatePicker,
  Select,
  Space,
  Empty,
  Tooltip,
  Typography,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import MotionHoc from "../../../utils/MotionHoc";
import { baseUrl, HEADERS_EXPORT } from "../../API/apiUrls";
import ReactECharts from "echarts-for-react";
import {
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileProtectOutlined,
  CheckOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  FilterOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";

//use redux
import axios from "axios";
import DateCustom from "../../../hook/DateCustom";
import dayjs from "dayjs";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import LoadLawyers from "../../../hook/LoadLawyers";
import DataCheck from "./modal/DataCheck";
import {
  enrichCase,
  exportCasesExcel,
  loadLoanByContno,
  STATUS_GROUPS,
} from "./lawyerCaseUtils";

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [convertDateThai, convertDateThaiShort] = DateCustom();
  const [loading, setLoading] = useState(false);
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const userCompany = localStorage.getItem("COMPANY_ID");
  const [cancelData, setCancelData] = useState([]);
  const [arrData, setArrData] = useState();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [isModalCheckData, setIsModalCheckData] = useState(false);
  const [statusData, setStatusData] = useState();
  const [isActive, setIsActive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lawyersList, setLoadingLawyers] = LoadLawyers();
  // ทนาย (ROLE_ID 3) เห็นเฉพาะของตัวเอง
  const [selectedLawyer, setSelectedLawyer] = useState(
    ROLE_ID === "3" ? userId : null,
  );
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("year"),
    dayjs(),
  ]);
  // query ล่าสุดที่โหลดสำเร็จ ใช้กับปุ่มรีโหลด
  const [lastQuery, setLastQuery] = useState(null);
  const [lawyerInfo, setLawyerInfo] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const lawyersOption = useMemo(() => {
    if (!Array.isArray(lawyersList)) return [];
    const companyIds =
      userCompany === "1" || userCompany === "2" ? [1, 2] : [3];
    return lawyersList
      .filter(
        (item) =>
          companyIds.includes(item.COMPANY_ID) &&
          item.ROLE_ID === 3 &&
          item.ACTIVE_STATUS === 1,
      )
      .map((item) => ({ value: item.id, label: item.NNAME }));
  }, [lawyersList, userCompany]);

  const isFilterChanged =
    !!lastQuery &&
    (lastQuery.lawyerId !== selectedLawyer ||
      !lastQuery.range[0].isSame(dateRange?.[0], "day") ||
      !lastQuery.range[1].isSame(dateRange?.[1], "day"));

  const datePresets = [
    { label: "เดือนนี้", value: [dayjs().startOf("month"), dayjs()] },
    {
      label: "เดือนที่แล้ว",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: "3 เดือนล่าสุด",
      value: [dayjs().subtract(3, "month").startOf("month"), dayjs()],
    },
    { label: "ปีนี้", value: [dayjs().startOf("year"), dayjs()] },
  ];

  useEffect(() => {
    setLoadingLawyers(true);
  }, [setLoadingLawyers]);

  // ทนายโหลดข้อมูลของตัวเองอัตโนมัติครั้งแรก
  useEffect(() => {
    if (selectedLawyer) {
      onSearch();
    }
  }, []);

  const onSearch = () => {
    if (!selectedLawyer) {
      message.warning("กรุณาเลือกทนาย");
      return;
    }
    if (!dateRange?.[0] || !dateRange?.[1]) {
      message.warning("กรุณาเลือกช่วงวันที่");
      return;
    }
    loadData(selectedLawyer, dateRange);
  };

  const onReload = () => {
    if (lastQuery) {
      loadData(lastQuery.lawyerId, lastQuery.range);
    }
  };

  useEffect(() => {
    BarChart();
  }, [cancelData]);

  const loadData = async (lawyerId, [start, end]) => {
    setLoading(true);
    setSelectedMonth(null);
    setSelectedData(null);

    try {
      const response = await axios.get(
        `${baseUrl}/lawyer/api/lawyers/${lawyerId}`,
        {
          headers: HEADERS_EXPORT,
          params: {
            startDate: start.format("YYYY-MM-DD"),
            endDate: end.format("YYYY-MM-DD"),
          },
        },
      );
      if (response.data) {
        console.log(response.data);
        filterDataLawyer(response.data);
        setLawyerInfo(Array.isArray(response.data) ? null : response.data);
        setLastQuery({ lawyerId, range: [start, end] });
        setLastLoadedAt(dayjs());
      }
      setLoading(false);
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response ? error.response.data : error.message,
      );
      setCancelData({});
      setLawyerInfo(null);
      setLoading(false);
      message.error(`ไม่พบข้อมูล`);
    }
  };

  // แปลง response ใหม่ (ทนาย -> work_lawsuits[]) ให้เป็นรายการแบบเดิม
  // คดีที่มีสถานะ (MAIN_STATUS_ID) > 2 ต้องมี วันฟ้อง หรือ ศาล หรือ เลขคดีดำ อย่างน้อย 1 อย่าง ไม่งั้นตัดออก
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text !== "" && text !== "-" && text.toLowerCase() !== "null";
  };

  const isValidLawsuit = (lawsuit) => {
    const hasStatusOver2 = (lawsuit.working_logs || []).some(
      (log) => Number(log.MAIN_STATUS_ID) > 2,
    );
    if (!hasStatusOver2) return true;
    return (
      (hasValue(lawsuit.date_of_plaint) &&
        dayjs(lawsuit.date_of_plaint).isValid()) ||
      hasValue(lawsuit.provincial_court) ||
      hasValue(lawsuit.black_case_number)
    );
  };

  const flattenLawyerData = (data) => {
    const lawyers = Array.isArray(data) ? data : data ? [data] : [];
    return lawyers.flatMap((lawyer) =>
      (lawyer.work_lawsuits || []).filter(isValidLawsuit).map((lawsuit) => {
        const logs = [...(lawsuit.working_logs || [])].sort(
          (a, b) => a.id - b.id,
        );
        const lastLog = logs[logs.length - 1];
        const judge = lawsuit.work_judges?.[0];
        return {
          ...lawsuit,
          LAWYER_NNAME: lawyer.NNAME,
          MAIN_STATUS_ID: lastLog?.MAIN_STATUS_ID,
          main_status_description: lastLog?.main_status_description,
          red_case_number: judge?.red_case_number || null,
          judge_date: judge?.judge_date || null,
          judgement: judge?.judgement || null,
          CUSTOMER_TNAME: lawsuit.customer_title,
          CUSTOMER_FNAME: lawsuit.customer_name,
          CUSTOMER_LNAME: lawsuit.customer_lastname,
        };
      }),
    );
  };

  const filterDataLawyer = (rawData) => {
    const data = flattenLawyerData(rawData);
    if (Array.isArray(data)) {
      // ✅ กรอง Role (Admin & ทนาย)
      const newData = data.filter(
        (item) =>
          ROLE_ID === "1" ||
          ROLE_ID === "3" ||
          ROLE_ID === "2" ||
          ROLE_ID === "8",
      );

      setArrData(newData);

      let groupedByLawyer = {};

      newData.forEach((item) => {
        const lawyerName = item.LAWYER_NNAME || "ไม่ระบุทนาย";

        // สร้างกลุ่มข้อมูลสำหรับทนายแต่ละคน
        if (!groupedByLawyer[lawyerName]) {
          groupedByLawyer[lawyerName] = {
            all: [], // ทั้งหมด (ใช้คำนวณ % / export ไม่แสดงในกราฟ)
            waitLawsuit: [], // รอฟ้อง (ไม่มีเลขคดี)
            withBackCase: [], // มีเลขคดีดำ + วันนัด
            withRedCase: [], // มีเลขคดีแดง MAIN_STATUS_ID === 4
            ageeement: [], // ยอมความ - MAIN_STATUS_ID === 5
            finish: [], // MAIN_STATUS_ID === 11
            withdrawCase: [], // MAIN_STATUS_ID === 13
          };
        }

        const group = groupedByLawyer[lawyerName];
        group.all.push(item);

        // แต่ละคดีอยู่ได้กลุ่มเดียว ตามสถานะล่าสุด (MAIN_STATUS_ID)
        if (item.MAIN_STATUS_ID === 13) {
          group.withdrawCase.push(item);
        } else if (item.MAIN_STATUS_ID === 11) {
          group.finish.push(item);
        } else if (item.MAIN_STATUS_ID === 5) {
          group.ageeement.push(item);
        } else if (item.MAIN_STATUS_ID === 4) {
          group.withRedCase.push(item);
        } else if (item.black_case_number && item.consideration_date) {
          group.withBackCase.push(item);
        } else if (!item.black_case_number) {
          group.waitLawsuit.push(item);
        }
      });

      console.log("groupedByLawyer", groupedByLawyer);
      setCancelData(groupedByLawyer);

      // แสดง "รายละเอียดข้อมูล" ของทนายคนแรกทันที ไม่ต้องคลิกกราฟ
      const firstLawyer = Object.keys(groupedByLawyer)[0];
      if (firstLawyer) {
        selectLawyerData(firstLawyer, groupedByLawyer[firstLawyer]);
      }
    } else {
      console.error("data is not an array or is undefined");
    }
  };

  const BarChart = () => {
    // ✅ 1) ใช้ Object.keys แทน map()
    const lawyerNames = Object.keys(cancelData).filter(Boolean);
    console.log("cancelData", cancelData);

    // ✅ 2) แต่ละแท่ง = 1 กลุ่มสถานะ (ไม่รวม "ทั้งหมด")
    const chartSeries = [
      { key: "waitLawsuit", name: "สร้างฟ้อง", color: "#ff4d4f" },
      { key: "withBackCase", name: "มีวันนัดศาล", color: "#fa8c16" },
      { key: "withRedCase", name: "ศาลตัดสิน/พิพากษา", color: "#1677ff" },
      { key: "ageeement", name: "ยอมความ", color: "#13c2c2" },
      { key: "finish", name: "จบคดี", color: "#52c41a" },
      { key: "withdrawCase", name: "ถอนฟ้อง", color: "#8c8c8c" },
    ];

    const chartData = [
      ["ทนาย", ...chartSeries.map((s) => s.name)],
      ...lawyerNames.map((lawyerName) => {
        const data = cancelData[lawyerName] || {};
        return [
          lawyerName,
          ...chartSeries.map((s) => data[s.key]?.length || 0),
        ];
      }),
    ];

    const option = {
      legend: { top: 0 },
      grid: { top: 50, left: 40, right: 40, bottom: 30 },
      toolbox: {
        show: true,
        feature: {
          saveAsImage: {},
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      dataset: {
        source: chartData,
      },
      xAxis: { type: "category" },
      yAxis: { type: "value", minInterval: 1 },
      series: chartSeries.map((s) => ({
        type: "bar",
        name: s.name,
        barMaxWidth: 48,
        label: {
          show: true,
          position: "top",
          formatter: (p) =>
            p.value[p.encode.y[0]] ? p.value[p.encode.y[0]] : "",
        },
        itemStyle: {
          color: s.color,
          borderRadius: [8, 8, 0, 0],
        },
      })),
    };
    return (
      <ReactECharts
        option={option}
        style={{ height: 400, width: "100%" }}
        onEvents={{ click: onChartClick }}
      />
    );
  };

  const selectLawyerData = (name, data) => {
    if (!data) return;
    setSelectedMonth(name);
    setSelectedData(data);
  };

  const onChartClick = (params) => {
    selectLawyerData(params.name, cancelData[params.name]);
  };

  const exportSubtitle = lastQuery
    ? `${selectedMonth || ""} | ช่วงวันที่ ${lastQuery.range[0].format(
        "DD/MM/YYYY",
      )} - ${lastQuery.range[1].format("DD/MM/YYYY")}`
    : selectedMonth || "";

  // export excel ตามตาราง แยก sheet ตามงาน + sheet สรุป
  const exportAllExcel = async () => {
    if (!selectedData?.all?.length) {
      message.warning("ไม่มีข้อมูลสำหรับ export");
      return;
    }
    setExporting(true);
    try {
      const loanByContno = await loadLoanByContno(selectedData.all);
      await exportCasesExcel({
        sheets: STATUS_GROUPS.map((group) => ({
          ...group,
          rows: (selectedData[group.key] || []).map((item) =>
            enrichCase(item, loanByContno),
          ),
        })),
        subtitle: exportSubtitle,
        formatDate: convertDateThaiShort,
        fileName: `รายงานคดี_${selectedMonth || ""}_${dayjs().format(
          "YYYYMMDD_HHmm",
        )}.xlsx`,
      });
    } catch (error) {
      console.error(error);
      message.error("Export Excel ไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  };

  const handleData = (status) => {
    setStatusData(status);
    setIsModalCheckData(true);
  };

  const tagStyle = {
    fontSize: "16px",
    padding: "10px 16px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: "bold",
    cursor: "pointer",
    transform: isActive ? "scale(0.96)" : "scale(1)",
    boxShadow: isActive ? "inset 0 2px 5px rgba(0,0,0,0.2)" : "none",
    transition: "transform 0.1s ease, box-shadow 0.1s ease",
  };

  if (ROLE_ID === "4") {
    return <Card>ไม่มีสิทธ์เข้าถึงข้อมูล</Card>;
  } else {
    return (
      <>
        <Card
          style={{
            marginBottom: 16,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          styles={{ body: { padding: 20 } }}
        >
          <Row
            justify="space-between"
            align="middle"
            gutter={[16, 8]}
            style={{ marginBottom: 16 }}
          >
            <Col>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <BarChartOutlined
                  style={{ color: "#3357FF", marginRight: 8 }}
                />
                รายงานผลงานทนาย
              </Typography.Title>
            </Col>
            <Col>
              {lastLoadedAt && (
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  อัปเดตล่าสุด {lastLoadedAt.format("DD/MM/YYYY HH:mm:ss")}
                </Typography.Text>
              )}
            </Col>
          </Row>

          <div
            style={{
              background: "#f7f9fc",
              border: "1px solid #e8edf5",
              borderRadius: 10,
              padding: 16,
            }}
          >
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} md={8}>
                <Typography.Text
                  strong
                  style={{ display: "block", marginBottom: 6 }}
                >
                  <UserOutlined style={{ marginRight: 6 }} />
                  ทนาย
                </Typography.Text>
                <Select
                  showSearch
                  allowClear={ROLE_ID !== "3"}
                  disabled={ROLE_ID === "3"}
                  placeholder="เลือกทนาย"
                  style={{ width: "100%" }}
                  optionFilterProp="label"
                  options={lawyersOption}
                  value={selectedLawyer}
                  onChange={(value) => setSelectedLawyer(value)}
                />
              </Col>
              <Col xs={24} md={9}>
                <Typography.Text
                  strong
                  style={{ display: "block", marginBottom: 6 }}
                >
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  ช่วงวันที่
                </Typography.Text>
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  presets={datePresets}
                  allowClear={false}
                  value={dateRange}
                  onChange={(value) => setDateRange(value)}
                />
              </Col>
              <Col xs={24} md={7}>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    loading={loading}
                    onClick={onSearch}
                  >
                    โหลดข้อมูล
                  </Button>
                  <Tooltip title="โหลดข้อมูลชุดล่าสุดอีกครั้ง">
                    <Button
                      icon={<ReloadOutlined spin={loading} />}
                      disabled={!lastQuery || loading}
                      onClick={onReload}
                    >
                      รีโหลด
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>
            {isFilterChanged && (
              <Tag
                icon={<FilterOutlined />}
                color="warning"
                style={{ marginTop: 12 }}
              >
                มีการเปลี่ยนตัวกรอง กด "โหลดข้อมูล" เพื่อแสดงผลใหม่
              </Tag>
            )}
          </div>

          {lawyerInfo && (
            <Space wrap size={[8, 8]} style={{ marginTop: 16 }}>
              <Tag color="blue" style={{ fontSize: 14, padding: "4px 10px" }}>
                <UserOutlined style={{ marginRight: 4 }} />
                {lawyerInfo.NNAME} ({lawyerInfo.FNAME} {lawyerInfo.LNAME})
              </Tag>

              <Tag
                color="geekblue"
                style={{ fontSize: 14, padding: "4px 10px" }}
              >
                <FileProtectOutlined style={{ marginRight: 4 }} />
                {arrData?.length || 0} คดี
              </Tag>
              {lastQuery && (
                <Tag style={{ fontSize: 14, padding: "4px 10px" }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  {lastQuery.range[0].format("DD/MM/YYYY")} -{" "}
                  {lastQuery.range[1].format("DD/MM/YYYY")}
                </Tag>
              )}
            </Space>
          )}
        </Card>

        <Card
          style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <Spin spinning={loading} size="large" tip=" Loading... ">
            {Object.keys(cancelData || {}).length > 0 ? (
              BarChart()
            ) : (
              <Empty
                style={{ padding: "60px 0" }}
                description={
                  lastQuery
                    ? "ไม่พบข้อมูลในช่วงวันที่ที่เลือก"
                    : 'เลือกทนายและช่วงวันที่ แล้วกด "โหลดข้อมูล"'
                }
              />
            )}

            <>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                loading={exporting}
                disabled={!selectedData?.all?.length}
                style={{ marginTop: 10, background: "#217346" }}
                onClick={exportAllExcel}
              >
                Export Excel (แยกตามงาน) {selectedMonth}
              </Button>

              <div
                style={{
                  marginTop: 24,
                  padding: "20px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                  background: "#f9f9f9",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                }}
              >
                <h3
                  style={{
                    marginBottom: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <BarChartOutlined
                    style={{ color: "#1890ff", fontSize: "20px" }}
                  />
                  รายละเอียดข้อมูล: <b>{selectedMonth}</b>
                </h3>

                {/* ส่วนที่ 1: สรุปข้อมูลทั้งหมด */}
                <div style={{ marginBottom: 16 }}>
                  <Tag
                    color="#3357FF"
                    style={{
                      ...tagStyle,
                      color: "#fff",
                      fontSize: "16px",
                      padding: "8px 16px",
                    }}
                    onClick={() => handleData("all")}
                    className="clickable-tag"
                    onMouseDown={() => setIsActive(true)}
                    onMouseUp={() => setIsActive(false)}
                    onMouseLeave={() => setIsActive(false)}
                  >
                    <InboxOutlined /> ทั้งหมด: {selectedData?.all?.length || 0}{" "}
                    สัญญา
                  </Tag>
                </div>

                {/* ส่วนที่ 2: สถานะคดี */}
                <div style={{ marginBottom: 12 }}>
                  <h4 style={{ marginBottom: 8, color: "#595959" }}>
                    📋 สถานะคดี
                  </h4>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    <Tag
                      color="#FFD699"
                      style={{ ...tagStyle, color: "#000" }}
                      onClick={() => handleData("waitLawsuit")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <ClockCircleOutlined /> สร้างฟ้อง:{" "}
                      {selectedData?.waitLawsuit?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.8,
                          }}
                        >
                          (
                          {(
                            (selectedData?.waitLawsuit?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>

                    <Tag
                      color="#FFB84D"
                      style={{ ...tagStyle, color: "#000" }}
                      onClick={() => handleData("withBackCase")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <CalendarOutlined /> มีวันนัดศาล:{" "}
                      {selectedData?.withBackCase?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.8,
                          }}
                        >
                          (
                          {(
                            (selectedData?.withBackCase?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>

                    <Tag
                      color="#4DFF88"
                      style={{ ...tagStyle, color: "#000" }}
                      onClick={() => handleData("withRedCase")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <CheckCircleOutlined /> พิพากษาแล้ว:{" "}
                      {selectedData?.withRedCase?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.8,
                          }}
                        >
                          (
                          {(
                            (selectedData?.withRedCase?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>

                    <Tag
                      color="#87D068"
                      style={{ ...tagStyle, color: "#fff" }}
                      onClick={() => handleData("ageeement")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <FileProtectOutlined /> ยอมความ:{" "}
                      {selectedData?.ageeement?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.9,
                          }}
                        >
                          (
                          {(
                            (selectedData?.ageeement?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>
                  </div>
                </div>

                {/* ส่วนที่ 3: สถานะเสร็จสิ้น */}
                <div>
                  <h4 style={{ marginBottom: 8, color: "#595959" }}>
                    ✅ สถานะเสร็จสิ้น
                  </h4>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    <Tag
                      color="#52C41A"
                      style={{ ...tagStyle, color: "#fff", marginBottom: 8 }}
                      onClick={() => handleData("finish")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <CheckOutlined /> จบคดี:{" "}
                      {selectedData?.finish?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.9,
                          }}
                        >
                          (
                          {(
                            (selectedData?.finish?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>
                  </div>
                </div>

                <div>
                  <h4 style={{ marginBottom: 8, color: "#595959" }}>
                    <CloseCircleOutlined /> อื่น ๆ
                  </h4>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    <Tag
                      color="#FF4D4F"
                      style={{ ...tagStyle, color: "#fff" }}
                      onClick={() => handleData("withdrawCase")}
                      className="clickable-tag"
                      onMouseDown={() => setIsActive(true)}
                      onMouseUp={() => setIsActive(false)}
                      onMouseLeave={() => setIsActive(false)}
                    >
                      <CloseCircleOutlined /> ถอนคดี:{" "}
                      {selectedData?.withdrawCase?.length || 0} สัญญา
                      {selectedData?.all?.length > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: "11px",
                            opacity: 0.9,
                          }}
                        >
                          (
                          {(
                            (selectedData?.withdrawCase?.length /
                              selectedData?.all?.length) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </Tag>
                  </div>
                </div>
              </div>
            </>
          </Spin>
        </Card>

        <DataCheck
          open={isModalCheckData}
          close={setIsModalCheckData}
          data={selectedData}
          status={statusData}
          lawyerName={selectedMonth}
          subtitle={exportSubtitle}
        />
      </>
    );
  }
};

const LawyersReport = MotionHoc(Main);
export default LawyersReport;
